import { useState, useEffect, useCallback } from 'react';
import { normalizeRuntimeSaveOutcome } from '../../../../app/runtime/saveOutcome';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';

export const useIntegrationConfig = (client, shopId) => {
  const t = useLocalizedMessage();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(async (mode = 'preview') => {
    try {
      setLoading(true);
      setError(null);
      const result = await client.loadSearchExperienceConfig(shopId, mode);
      setConfig(result);
      return result;
    } catch (err) {
      setError(err.message || t('search.settings.config.loadFailed'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [client, shopId, t]);

  useEffect(() => {
    if (client && shopId) {
      loadConfig();
    }
  }, [client, shopId, loadConfig]);

  const saveConfig = useCallback(async (updatedConfig) => {
    try {
      const result = await client.saveSearchExperienceConfig(shopId, updatedConfig, 'preview');
      const outcome = normalizeRuntimeSaveOutcome(result, updatedConfig);
      if (!outcome.success || outcome.errors.length > 0) {
        throw new Error(
          outcome.errors[0]?.message
          || result?.error
          || t('search.settings.config.saveRejected'),
        );
      }
      setConfig(outcome.config || updatedConfig);
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || t('search.settings.config.saveFailed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [client, shopId, t]);

  const updateViewIntegration = useCallback((view, integration) => {
    if (!config) return null;

    // Use spread-based immutable update at every level so frozen objects
    // (e.g. values passed through Redux/RTK state) are never mutated directly.
    return {
      ...config,
      search: {
        ...config.search,
        views: {
          ...(config.search?.views),
          [view]: {
            ...(config.search?.views?.[view]),
            integration,
          },
        },
      },
    };
  }, [config]);

  return {
    config,
    loading,
    error,
    loadConfig,
    saveConfig,
    updateViewIntegration,
  };
};
